import { useMemo } from 'react'
import UserAvatar from '../Shared/UserAvatar'
import '../../styles/data-table.css'

/** Colores del estado (hex para derivar el fondo suave del badge). */
const STATUS_COLORS = { active: 'var(--gm-success)', inactive: 'var(--gm-danger)' }

/**
 * Componente de tabla para la visualización y gestión de usuarios.
 * * **Funcionalidades**:
 * - Ordenamiento automático: Los usuarios se ordenan alfabéticamente por nombre mediante `useMemo`.
 * - Estado de carga: Muestra filas con un efecto "shimmer" (esqueleto) mientras `loading` es verdadero.
 * - Diseño compacto: usa las clases compartidas de `data-table.css` (tokens --gm-*, claro/oscuro).
 * - Interactividad: Permite seleccionar un usuario haciendo clic en la fila o en el botón de acción.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} props.users - Lista de objetos de usuario a mostrar.
 * @param {boolean} props.loading - Indica si los datos están en proceso de carga.
 * @param {Function} props.selectedUser - Callback disparado al seleccionar un usuario. Recibe el objeto `user`.
 * * @returns {JSX.Element} Una tabla compacta con manejo de estados.
 */
const UserTable = ({ users, loading, selectedUser }) => {
  /**
   * Memoriza la lista de usuarios ordenada alfabéticamente por el campo 'nombre'.
   * Se recalcula solo cuando la lista original de usuarios cambia.
   */
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) =>
      a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
    )
  }, [users])

  return (
    <div className="tt" style={{ flex: 'none' }}>
      <div className="tt-scroll">
        <table className="tt-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx}>
                  <td colSpan="5">
                    <div className="ut-shimmer" />
                  </td>
                </tr>
              ))
            ) : sortedUsers.length > 0 ? (
              sortedUsers.map((user) => {
                const color = user.activo
                  ? STATUS_COLORS.active
                  : STATUS_COLORS.inactive
                return (
                  <tr
                    key={user.id}
                    onClick={() => selectedUser(user)}
                    className="tt-row"
                  >
                    <td>
                      <span className="tt-person" style={{ maxWidth: 260 }}>
                        <UserAvatar employee={user} size={22} />
                        <span className="tt-ellipsis fw-semibold">
                          {user.nombre} {user.apellido}
                        </span>
                      </span>
                    </td>
                    <td className="tt-muted">{user.email}</td>
                    <td
                      className="tt-muted"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {user.telefono || '—'}
                    </td>
                    <td>
                      <span
                        className="tt-badge gm-tono"
                        style={{ '--tono': color }}
                      >
                        {user.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="tt-actions">
                        <button
                          type="button"
                          className="tt-icon-btn"
                          title="Ver detalle"
                          onClick={(e) => {
                            e.stopPropagation()
                            selectedUser(user)
                          }}
                        >
                          <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="5" className="tt-empty">
                  <i className="fa-solid fa-user-slash d-block fs-5 mb-2 opacity-50" />
                  No se encontraron usuarios en el directorio
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .ut-shimmer {
          height: 14px;
          border-radius: 4px;
          background: linear-gradient(90deg, var(--gm-surface-2) 25%, var(--gm-border) 50%, var(--gm-surface-2) 75%);
          background-size: 200% 100%;
          animation: ut-shimmer 1.4s infinite;
        }
        @keyframes ut-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  )
}

export default UserTable
